import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
  Text,
} from '@cinch-labs/design-system'
import { SoldByPartnerInfo } from '@cinch-nx/ui-new-cars-drawer'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import Image from 'next/image'
import { ReactNode, useId, useMemo } from 'react'
import styles from './finance-offers-carousel.module.css'
import cx from 'classnames'

export interface FinanceOffersCarouselProps {
  title: string
  description: string
  disclaimer: string
  cards: ReactNode[]
  placeHolderImage: {
    src: string
    alt: string
  }
  financeRepresentativeExample?: ReactNode
}

export function FinanceOffersCarousel({
  title,
  description,
  disclaimer,
  cards,
  placeHolderImage,
  financeRepresentativeExample,
}: FinanceOffersCarouselProps) {
  const reactId = useId()
  const carouselId = useMemo(
    () => 'finance-offers-carousel-' + reactId.replace(/:/g, ''),
    [reactId],
  )

  const { isMobile } = useResponsiveEnvironment()
  const placeHolderSlide =
    placeHolderImage.src && cards.length === 1 && !isMobile ? (
      <div className={styles.placeholderImageCard}>
        <Image
          src={placeHolderImage.src}
          alt={''}
          fill={true}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </div>
    ) : undefined

  const slides = [...cards, ...(placeHolderSlide ? [placeHolderSlide] : [])]

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        {title && (
          <Text
            element={'h2'}
            fontSizeFixed={'xl'}
            fontWeight={'bold'}
            className={styles.title}
          >
            {title}
          </Text>
        )}
        {description && (
          <Text className={styles.description}>{description}</Text>
        )}
        {disclaimer && (
          <div className={styles.disclaimerContainer}>
            <SoldByPartnerInfo callToActionText={disclaimer} />
          </div>
        )}
      </div>
      <Carousel
        name={carouselId}
        swiperSettings={{
          slidesPerView: 'auto',
          spaceBetween: 8,
          centerInsufficientSlides: true,
          breakpoints: {
            768: {
              spaceBetween: 32,
            },
          },
        }}
      >
        {slides?.map((slide, index) => (
          <CarouselSlide
            key={`${carouselId}-offer-card-${index}`}
            className={cx(
              styles.carouselSlide,
              slides.length === 2 ? styles.largerCarouselSlide : '',
            )}
          >
            {slide}
          </CarouselSlide>
        ))}
      </Carousel>
      <CarouselControls>
        <CarouselPagination name={carouselId} />
        <CarouselNavigation
          name={carouselId}
          ariaLabels={{
            previous: 'Go to previous slide',
            next: 'Go to next slide',
          }}
        />
      </CarouselControls>
      {financeRepresentativeExample}
    </section>
  )
}
